
import { GroupedNotesModel, GroupedVideoModel, NoteModel } from '@/models/student-notes/note.model';
import { ClassroomModel, CourseModel, UserModel } from '@descomplica/dex-models';
import Vue from 'vue';
import CardSkeleton from '../components/skeletons/atoms/CardSkeleton.vue';
import LineSkeleton from '../components/skeletons/atoms/LineSkeleton.vue';
import ShareModal from '../components/modals/ShareModal.vue';
import { secondsToTimestamp } from '@/helpers/date';
import {
  AnnotationInputCard,
  Button,
  Icon,
  LessonDetailsCard,
  MenuTree,
  SelectGrouped,
  Toast,
  ToggleSwitch,
  UserSettingsModal,
} from '@descomplica/vue-components';
import { UserSettingsModel } from '@/models/alumni-profile/user-settings.model';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getCourseCategory } from '@/helpers/category';
import i18n from '@/i18n';
import { TranslateResult } from 'vue-i18n';
import { SharedNotebook } from '@/models/student-notes/shared-notebook.model';

interface NoteToast {
  isVisible: boolean;
  success?: boolean;
  error?: boolean;
  description?: TranslateResult;
}

type OptionType = {
  title: string;
  count: number;
};

type ItemType = {
  option: string;
  subOptions: OptionType[];
  isOpened?: boolean;
  count: number;
};

interface GroupedVideos {
  video: string;

  notes: NoteModel[];
}

interface Data {
  isConfigurationModalActive?: boolean;
  isShareModalActive?: boolean;
  shouldAutoPauseVideo?: boolean;
  optionSelected: string;
  subOptionSelected: OptionType;
  menuItems: ItemType[];
  noteToast: NoteToast;
  timeouts: Array<number>;
}

export default Vue.extend({
  components: {
    UserSettingsModal,
    ShareModal,
    AnnotationInputCard,
    LessonDetailsCard,
    ToggleSwitch,
    CardSkeleton,
    LineSkeleton,
    SelectGrouped,
    MenuTree,
    Button,
    Icon,
    Toast,
  },
  props: {
    otherStudentSharedNotebook: {
      type: Object as Vue.PropType<SharedNotebook>,
      required: false,
      default: null,
    },
    disciplineId: {
      type: Number,
      required: false,
      default: undefined,
    },
    disciplineSlug: {
      type: String,
      required: false,
      default: '',
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
    avoidRedirection: {
      type: Boolean,
      default: false,
    },
    avoidDexFieldsUpdate: {
      type: Boolean,
      default: false,
    },
    pageTitle: {
      type: String,
      default: i18n.t('notebooksPageTitle'),
    },
  },
  data(): Data {
    return {
      optionSelected: '',
      subOptionSelected: {} as OptionType,
      menuItems: [],
      isConfigurationModalActive: false,
      isShareModalActive: false,
      shouldAutoPauseVideo: false,
      timeouts: [],
      noteToast: {
        isVisible: false,
      },
    };
  },
  computed: {
    classroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    alumniProfile(): UserModel {
      return this.$store.getters.currentAlumniProfile;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    shouldShowAuxiliarButtons(): boolean {
      return !this.hideButtons && !this.isLoading && !this.isNotesEmpty;
    },
    groupedNotes(): GroupedNotesModel[] {
      return this.$store.getters.getNotebookNotes;
    },
    selectedNotes(): GroupedVideos[] {
      const discipline = this.disciplineId ? this.classroom.name : this.optionSelected;

      const lesson = this.disciplineId ? this.optionSelected : this.subOptionSelected?.title;

      const video =
        this.disciplineId && this.subOptionSelected?.title ? this.subOptionSelected?.title : null;

      const selectedNotes = this.$store.getters.getFilteredNotebookNotes(discipline, lesson, video);

      return selectedNotes;
    },
    menuOptions(): ItemType[] {
      if (this.groupedNotes.length === 0) return [];

      if (!this.disciplineId) {
        return this.groupedNotes?.map((group) => ({
          option: group.discipline,
          subOptions: group.lessons.map((lesson: any) => ({
            title: lesson.lesson,
            count: lesson.videos.reduce((acc: any, video: any) => {
              return acc + video.notes.length;
            }, 0),
          })),
          count: group.totalNotes,
          isOpened: false,
        }));
      }

      const discipline = this.groupedNotes?.filter(
        (grouped) => grouped.discipline === this.classroom.name,
      );

      return discipline[0].lessons.map((lesson) => ({
        option: lesson.lesson,
        subOptions: lesson.videos.map((video: any) => ({
          title: video.video,
          count: video.notes.length,
        })),
        count: lesson.videos.reduce((acc: any, video: any) => {
          return acc + video.notes.length;
        }, 0),
        isOpened: false,
      }));
    },
    emptyNotebooksIcon(): string {
      return `${process.env.BASE_URL}img/empty-notebooks.svg`;
    },
    emptySharedNotebookIcon(): string {
      return `${process.env.BASE_URL}img/empty-shared-notebook.svg`;
    },
    isLoading(): boolean {
      return this.$store.getters.isLoadingNotebookNotes;
    },
    isGeneratingSharedNotebookLink(): boolean {
      return this.$store.getters.isGeneratingSharedNotebookLink;
    },
    isNotesEmpty(): boolean {
      return !this.isLoading && this.groupedNotes.length === 0;
    },
    userSettings(): UserSettingsModel {
      return this.$store.getters.currentUserSettings;
    },
    optionTitle(): string {
      return this.optionSelected;
    },
    subTitle(): string {
      if (!this.disciplineId) {
        return this.subOptionSelected?.title;
      }

      return this.subOptionSelected?.title ? this.subOptionSelected?.title : this.optionSelected;
    },
    isSharedNotebookRoute(): boolean {
      return !!this.$route.params.notebookHash;
    },
    isSharedNotebookHashValid(): boolean {
      return this.isSharedNotebookRoute
        ? this.isSharedNotebookRoute && !!this.otherStudentSharedNotebook
        : true;
    },
    shareTitle(): TranslateResult {
      return this.disciplineId ? i18n.t('shareNotes') : i18n.t('share');
    },
    totalDisciplineNotes(): number {
      const discipline: ItemType | undefined = this.menuOptions.find(
        (menuOption) => menuOption.option === this.optionSelected,
      );

      return discipline?.count || 0;
    },
    sharedNotebook(): SharedNotebook {
      return this.$store.getters.getSharedNotebook;
    },
    shareableUrl(): string {
      return `${window.location.host}/caderno-compartilhado/${this.sharedNotebook?.generatedLink}`;
    },
    handleGeneratedLinkUrl(): TranslateResult | string {
      return this.isGeneratingSharedNotebookLink ? i18n.t('generatingLink') : this.shareableUrl;
    },
  },
  watch: {
    menuOptions(menuOptions) {
      this.menuItems = menuOptions;
    },
    shouldAutoPauseVideo(value, oldValue) {
      if (oldValue !== undefined && this.userSettings?.shouldAutoPauseVideo !== value)
        this.$store.dispatch('EDIT_USER_SETTINGS', value);
    },
  },
  created() {
    this.updateNotesDexFields();

    Analytics.trackEvent(EVENT_LABELS.notebookOpened, {
      category: getCourseCategory(this.currentCourse),
      course: this.currentCourse.name,
    });
  },
  mounted() {
    this.shouldAutoPauseVideo = this.userSettings?.shouldAutoPauseVideo;
  },
  beforeDestroy() {
    const { clearTimeout } = window;

    this.timeouts.map((timeout) => clearTimeout(timeout));
  },
  methods: {
    getLessonName(notes: NoteModel[]): string {
      if (notes.length === 0 || !notes[0].dexLesson) return 'Aula';

      return notes[0].dexLesson.name;
    },
    getSecondsToTimestamp(seconds: number): string {
      return secondsToTimestamp(seconds);
    },
    toggleConfigurationModal(active: boolean): void {
      this.isConfigurationModalActive = active;
    },
    toggleShareModal(active: boolean): void {
      this.isShareModalActive = active;
    },
    async updateNotesDexFields(): Promise<void> {
      if (!this.avoidDexFieldsUpdate) {
        await this.$store.dispatch('UPDATE_USER_NOTES_DEX_FIELDS', {
          profileId: this.alumniProfile.id,
          courseId: this.currentCourse.id,
        });
      }

      if (this.isSharedNotebookRoute) {
        this.pullUserSharedNotes();
        return;
      }
      this.pullUserNotes();
    },
    async pullUserNotes(): Promise<void> {
      await this.$store.dispatch('GET_ALL_USER_NOTES', {
        profileId: this.alumniProfile.id,
        dexDisciplineId: this.disciplineId,
      });
    },
    async pullUserSharedNotes(): Promise<void> {
      if (!this.otherStudentSharedNotebook) return;
      await this.$store.dispatch('GET_ALL_USER_NOTES', {
        profileId: this.otherStudentSharedNotebook.profileId,
        dexDisciplineId: this.otherStudentSharedNotebook.dexDisciplineId,
      });
    },
    firstCurrentNote(): NoteModel {
      const videoGrouped = this.selectedNotes[0];

      return videoGrouped.notes[0];
    },
    toggleOption(option: string): void {
      this.optionSelected = option;

      const annotation = this.firstCurrentNote();

      const interactionType = this.disciplineId ? 'Class Clicked' : 'Discipline Clicked';

      this.createAnalyticsEvent(annotation, interactionType);
    },
    toggleSubOption(subOption: OptionType): void {
      this.subOptionSelected = subOption;

      if (!subOption) return;

      const annotation = this.firstCurrentNote();

      const interactionType = this.disciplineId ? 'Video Clicked' : 'Class Clicked';

      this.createAnalyticsEvent(annotation, interactionType);
    },
    toggleSelectOption(option: string, subOption: OptionType): void {
      this.optionSelected = option;
      this.subOptionSelected = subOption;

      const annotation = this.firstCurrentNote();

      const interactionType = this.disciplineId ? 'Video Clicked' : 'Class Clicked';

      this.createAnalyticsEvent(annotation, interactionType);
    },
    getNoteWithVideoUrl(video: GroupedVideoModel): NoteModel | undefined {
      const noteWithUrl = video.notes.find((note) => !!note.originUrl);
      return noteWithUrl;
    },
    shouldClickLessonDetails(video: GroupedVideoModel): boolean {
      const note = this.getNoteWithVideoUrl(video);

      return this.avoidRedirection ? false : !!note;
    },
    onLessonDetailsClick(video: GroupedVideoModel): void {
      if (this.avoidRedirection) return;
      const note = this.getNoteWithVideoUrl(video);
      if (note) {
        this.createAnalyticsEvent(note, 'Note Origin Redirected');
        this.$router.push({
          path: `${note.originUrl}`,
        });
      }
    },
    createAnalyticsEvent(annotation: NoteModel, interactionType: string): void {
      Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
        category: getCourseCategory(this.currentCourse),
        course: this.currentCourse.name,
        lesson: annotation.dexLesson?.name,
        discipline: annotation.dexDiscipline?.name,
        interaction_type: interactionType,
        content_name: annotation.dexVideo?.name,
        content_type: 'video',
      });
    },
    async shareDiscipline(): Promise<void> {
      const firstAnnotation: NoteModel = this.selectedNotes[0].notes[0];

      if (firstAnnotation.dexDiscipline) {
        Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
          category: getCourseCategory(this.currentCourse),
          course: this.currentCourse.name,
          discipline: firstAnnotation.dexDiscipline?.name,
          interaction_type: 'Share Button Clicked',
        });

        this.isShareModalActive = true;
        await this.$store.dispatch('SET_SHARED_NOTEBOOK', firstAnnotation.dexDiscipline?.id);
        return;
      }

      this.noteToast = {
        success: false,
        error: true,
        isVisible: true,
        description: i18n.t('errorShareNotes'),
      };

      const { setTimeout } = window;

      this.timeouts.push(
        setTimeout(() => {
          this.noteToast.isVisible = false;
        }, 2000),
      );
    },
    handleCopiedLink(): void {
      const firstAnnotation: NoteModel = this.selectedNotes[0].notes[0];

      Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
        category: getCourseCategory(this.currentCourse),
        course: this.currentCourse.name,
        discipline: firstAnnotation.dexDiscipline?.name,
        interaction_type: 'Copy Link Clicked',
      });
    },
    shouldDisplayLessonName(videoIndex: number, video: GroupedVideoModel): boolean {
      return (
        videoIndex === 0 ||
        (videoIndex - 1 >= 0 &&
          this.getLessonName(this.selectedNotes[videoIndex - 1].notes) !==
            this.getLessonName(video.notes))
      );
    },
  },
});
